import { useMutation } from '@apollo/client';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { gql } from '__generated__/gql';
import { FC, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import ErrorMessage from 'components/ErrorMessage';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogSubTitle, DialogTitle } from 'primitives/Dialog';

const REQUEST_CONTRIBUTION_AGREEMENT_MUTATION = gql(`
  mutation RequestContributionAgreement($investmentEntityId: ID!) {
    requestContributionAgreement(investmentEntityId: $investmentEntityId) {
      id
      signingStatus
      signingLinkUrl
    }
  }
`);

const ExplainerLink: FC<{ to: string; children: ReactNode }> = ({ to, children }) => (
  <Link to={to} className="text-indigo-600 hover:underline">
    {children}
  </Link>
);

const ContributionAgreementExplainer: FC = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
      <ol
        className={`list-decimal space-y-1 pl-6 marker:text-gray-600 ${
          expanded ? '' : 'max-h-56 overflow-hidden'
        }`}
      >
        <li>
          <strong className="font-semibold text-gray-900">What this agreement covers.</strong>
          <ul className="list-[circle] space-y-1 pl-6">
            <li>
              It sets out the terms on which you commit capital to AL Trust and receive units in the
              Fund. It is made between you, Vistra ITCL (India) Limited (Trustee) and AngelList
              India LLP (Investment Manager).
            </li>
            <li>
              You make a Capital Commitment, and the Investment Manager calls capital through
              Drawdown Notices for each investment you have approved.
            </li>
            <li>It replaces any earlier contribution agreement between the parties.</li>
          </ul>
        </li>
        <li>
          <strong className="font-semibold text-gray-900">
            In line with the recent SEBI changes.
          </strong>{' '}
          It reflects the SEBI (AIF) Second Amendment Regulations, 2025, including the Accredited
          Investor definition. Under the 2025 amendments, the Fund is classified as a Category-I AIF
          – Angel Fund.
        </li>
        <li>
          <strong className="font-semibold text-gray-900">Accreditation.</strong> Accreditation
          under the AIF Regulations becomes mandatory for investing in the Fund after 31 March 2027.
          You can get accredited here:{' '}
          <ExplainerLink to="/invest/accreditation">Get accredited</ExplainerLink>
        </li>
        <li>
          <strong className="font-semibold text-gray-900">
            Fund category and the Investment Manager’s role.
          </strong>{' '}
          AL Trust is registered with SEBI as a Category-I AIF (Angel Fund), with Vistra ITCL
          (India) Limited as Trustee. AngelList India, as Investment Manager, manages and
          administers the Fund’s Investments. It evaluates each opportunity and retains complete
          discretion over investment decisions, but capital is accepted only after you approve a
          deal.
        </li>
        <li>
          <strong className="font-semibold text-gray-900">Annexures to complete:</strong>
          <ul className="list-[circle] space-y-1 pl-6">
            <li>
              <strong className="font-semibold text-gray-900">
                Annexure A (Investor Data Sheet):
              </strong>{' '}
              fill in and sign it, including entity type, address and PAN. You confirm your KYC
              details are true, correct and valid.
            </li>
            <li>
              <strong className="font-semibold text-gray-900">Annexure B (Nomination Form):</strong>{' '}
              individuals must also fill in and sign nominee details. Units pass to your nominee, or
              to your legal successors if there is no nomination, provided they meet the Eligible
              Angel Investor requirements.
            </li>
          </ul>
        </li>
        <li>
          You confirm you have read the Private Placement Memorandum (PPM) and agree to its terms,
          which form an integral part of this agreement.{' '}
          <a
            href="/samples/AL_TRUST_PPM.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Download the PPM
          </a>
        </li>
      </ol>
      <button
        type="button"
        onClick={() => setExpanded(previous => !previous)}
        aria-expanded={expanded}
        className="mt-3 flex items-center gap-1 font-medium text-indigo-600 hover:underline"
      >
        {expanded ? 'Show less' : 'Show more'}
        {expanded ? (
          <ChevronUpIcon className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
};

const ContributionAgreementDialog: FC<{
  investmentEntityId: string;
  open: boolean;
  onClose: () => void;
}> = ({ investmentEntityId, open, onClose }) => {
  const [signingError, setSigningError] = useState<Error | null>(null);
  const [alreadySigned, setAlreadySigned] = useState(false);

  const [requestContributionAgreement, { loading }] = useMutation(
    REQUEST_CONTRIBUTION_AGREEMENT_MUTATION
  );

  const openSigningLink = async () => {
    setSigningError(null);

    // Opened synchronously, then pointed at the link once it exists: browsers block a
    // window.open() that happens after an await.
    const signingWindow = window.open('', '_blank');

    try {
      const result = await requestContributionAgreement({ variables: { investmentEntityId } });

      // The backend re-checks Leegality on every request, so a signature we hadn't heard about
      // yet comes back as SIGNED instead of a link.
      if (result.data?.requestContributionAgreement?.signingStatus === 'SIGNED') {
        signingWindow?.close();
        setAlreadySigned(true);
        return;
      }

      const signingLinkUrl = result.data?.requestContributionAgreement?.signingLinkUrl;

      if (!signingLinkUrl)
        throw new Error('We could not generate your signing link. Please try again.');

      if (signingWindow) signingWindow.location.href = signingLinkUrl;
      else window.location.href = signingLinkUrl;
    } catch (error) {
      signingWindow?.close();
      setSigningError(error as Error);
    }
  };

  if (alreadySigned) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogPanel className="sm:min-w-[560px]">
          <DialogTitle>Contribution Agreement signed</DialogTitle>
          <DialogSubTitle>
            We have your signature on the Contribution Agreement for this investment entity. No
            further action is needed.
          </DialogSubTitle>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </DialogPanel>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogPanel className="sm:min-w-[560px]">
        <DialogTitle>Sign your Contribution Agreement</DialogTitle>
        <DialogSubTitle>
          Please review and e-sign the Contribution Agreement for this investment entity. It opens
          in a new tab and takes a couple of minutes.
        </DialogSubTitle>
        <ContributionAgreementExplainer />
        {signingError && (
          <ErrorMessage
            type="alert"
            title="Unable to open the signing page"
            message={signingError.message}
            error={signingError}
            showLoginButton={false}
          />
        )}
        <DialogActions>
          <Button variant="secondary" onClick={onClose}>
            Remind me later
          </Button>
          <Button onClick={openSigningLink} loading={loading}>
            Sign now
          </Button>
        </DialogActions>
      </DialogPanel>
    </Dialog>
  );
};

export default ContributionAgreementDialog;
